import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { CalendarDays, ChevronRight, UsersRound } from 'lucide-react'
import { Button, Card, SearchField, StatusBadge } from '@shared/design-system/ui'
import { button as buttonRecipe } from '@shared/design-system/ui/Button/Button.css'
import { cx } from '@shared/lib/cx'
import { useSession } from '@shared/lib/auth/session-store'
import { hasAdminPermission } from '@shared/lib/auth/permissions'
import { useAdminUserSearch, useAdminUsers } from '../api/admin.api'
import type { AdminLookupUser } from '../api/admin.api'
import { filters, header, page, subtitle, title, titleBlock } from './admin.css'
import {
  actionControl,
  actionsCell,
  actionsHeader,
  avatar,
  identityCell,
  identityCopy,
  listHeader,
  listHeading,
  listState,
  listSubtitle,
  listSummary,
  listTitle,
  loadMore,
  registeredAt,
  statusGrid,
  statusHeader,
  statusItem,
  statusLabel,
  summaryItem,
  tableBody,
  tableHeader,
  userEmail,
  userName,
  userRow,
  usersCard,
  usersTable,
} from './AdminUsersScreen.css'

const documentationPresentation = {
  NAO_INICIADO: { label: 'Sem conta Stripe', tone: 'neutral' },
  PENDENTE: { label: 'Cadastro em andamento', tone: 'awaiting' },
  APROVADO: { label: 'Liberado para receber', tone: 'success' },
  REPROVADO: { label: 'Rejeitado pela Stripe', tone: 'closed' },
} as const

export function AdminUsersScreen() {
  const session = useSession((state) => state.user)
  const canRead = hasAdminPermission(session, 'USER_READ')
  const users = useAdminUsers(canRead)
  const [searchQuery, setSearchQuery] = useState('')
  const search = useAdminUserSearch(canRead ? searchQuery : '')
  const searching = searchQuery.trim().length >= 2
  const items = searching
    ? (search.data?.items ?? [])
    : (users.data?.pages.flatMap((page) => page.items) ?? [])
  const loading = users.isLoading || search.isLoading

  return (
    <section className={page} data-testid="users-screen">
      <div className={header}>
        <div className={titleBlock}>
          <h1 className={title}>Usuários</h1>
          <p className={subtitle}>
            Consulte contas, restrições operacionais e documentação cadastral.
          </p>
        </div>
      </div>

      <Card padding="md">
        <div className={filters}>
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            ariaLabel="Pesquisar usuários"
            placeholder="Pesquise por nome, e-mail ou ID"
          />
        </div>
      </Card>

      <Card padding="none" className={usersCard}>
        <div className={listHeader}>
          <div className={listHeading}>
            <h2 className={listTitle}>Usuários cadastrados</h2>
            <p className={listSubtitle}>
              {searching ? `Resultados para “${searchQuery.trim()}”` : 'Cadastros mais recentes.'}
            </p>
          </div>

          <div
            className={listSummary}
            aria-label={`${items.length} ${items.length === 1 ? 'usuário exibido' : 'usuários exibidos'}`}
          >
            <span className={summaryItem}>
              <UsersRound size={14} aria-hidden="true" />
              {items.length} {items.length === 1 ? 'usuário exibido' : 'usuários exibidos'}
            </span>
          </div>
        </div>

        <div className={usersTable} role="table" aria-label="Usuários cadastrados">
          <div className={tableHeader} role="row">
            <span role="columnheader">Usuário</span>
            <div className={statusHeader} role="presentation">
              <span role="columnheader">Verificação</span>
            </div>
            <span className={actionsHeader} role="columnheader">
              Ações
            </span>
          </div>

          <div className={tableBody} role="rowgroup">
            {canRead && !loading
              ? items.map((user) => <UserRow key={user.id} user={user} />)
              : null}

            {!canRead ? (
              <div className={listState} role="status">
                Seu acesso não permite consultar usuários.
              </div>
            ) : loading ? (
              <div className={listState} role="status">
                Carregando usuários...
              </div>
            ) : items.length === 0 ? (
              <div className={listState} role="status">
                {searching
                  ? 'Nenhum usuário encontrado para esta busca.'
                  : 'Nenhum usuário cadastrado.'}
              </div>
            ) : null}

            {!searching && users.hasNextPage ? (
              <Button
                className={loadMore}
                variant="secondary"
                disabled={users.isFetchingNextPage}
                onClick={() => void users.fetchNextPage()}
              >
                {users.isFetchingNextPage ? 'Carregando...' : 'Carregar mais'}
              </Button>
            ) : null}
          </div>
        </div>
      </Card>
    </section>
  )
}

function UserRow({ user }: { user: AdminLookupUser }) {
  const documentation =
    documentationPresentation[user.documentationStatus as keyof typeof documentationPresentation] ??
    documentationPresentation.NAO_INICIADO
  const displayName = user.name?.trim() || user.email
  return (
    <div className={userRow} role="row">
      <div className={identityCell} role="cell">
        <span className={avatar} aria-hidden="true">
          {userInitials(displayName)}
        </span>
        <div className={identityCopy}>
          <span className={userName}>{displayName}</span>
          <span className={userEmail}>{user.email}</span>
          <span className={registeredAt}>
            <CalendarDays size={12} aria-hidden="true" />
            Cadastrado em {formatDateTime(user.createdAt)}
          </span>
        </div>
      </div>

      <div className={statusGrid} role="presentation">
        <div className={statusItem} role="cell">
          <span className={statusLabel}>Recebimento</span>
          <StatusBadge size="sm" label={documentation.label} tone={documentation.tone} />
        </div>
      </div>

      <div className={actionsCell} role="cell">
        <Link
          className={cx(buttonRecipe({ variant: 'secondary', size: 'sm' }), actionControl)}
          aria-label={`Ver detalhes do usuário ${displayName}`}
          to="/usuarios/$userId"
          params={{ userId: user.id }}
        >
          Ver usuário
          <ChevronRight size={15} aria-hidden="true" />
        </Link>
      </div>
    </div>
  )
}

function userInitials(value: string): string {
  const parts = value.trim().split(/\s+/).filter(Boolean)

  if (parts.length === 0) return 'US'
  const first = parts[0] ?? ''
  if (parts.length === 1) return first.slice(0, 2).toLocaleUpperCase('pt-BR')
  return `${first[0] ?? ''}${parts.at(-1)?.[0] ?? ''}`.toLocaleUpperCase('pt-BR')
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value))
}
